import { useEffect, useState } from "react";

import { IEmailTemplateType } from "../../../@types/admin/AdminEmailTemplate";
import { useSaveEmailTemplate } from "../../../hooks/mutations/admin/emailTemplates";
import { useEmailTemplate } from "../../../hooks/queries/admin/emailTemplates";
import {
  useHospitalOptions,
  useWardOptions,
} from "../../../hooks/queries/admin/selectValues";
import { useModal } from "../../../hooks/useModal";
import { useToast } from "../../../hooks/useToast";
import { errorToastMessage } from "../../../utils/HttpError";
import {
  AdminSelectField,
  CheckboxField,
  ErrorMessage,
  FieldHint,
  FilterButton,
  FilterGroup,
  FilterInput,
  FilterLabel,
  FiltersContainer,
  HtmlPreview,
  LongTextEditor,
  ModalContainer,
  ModalForm,
  ModalFormActions,
  ModalTitle,
  PageContainer,
  PageHeader,
  PageTitle,
  SplitPane,
  WarningNotice,
} from "../shared";
import {
  SelectionPrompt,
  SubjectField,
  SubjectRow,
  TemplateCard,
  TemplateCardActionRow,
  TemplateCardActions,
  TemplateCardFooter,
  TemplateOrigin,
  VisibilityControl,
  VisibilityField,
} from "./AdminSettings.styles";
import { SendTestEmailModal } from "./SendTestEmailModal";

const TEMPLATE_TYPES: IEmailTemplateType[] = ["Enrolment", "Archive"];

/**
 * Placeholders a template may reference, from Retool's `availableVariables`
 * on the HTML editor widget. Not served by an endpoint — this list is the
 * only record of them, so it is carried over verbatim.
 */
const TEMPLATE_VARIABLES = [
  "patientFirstName",
  "patientLastName",
  "patientEmail",
  "hospitalName",
  "hospitalGroupName",
  "appStoreUrl",
  "googlePlayUrl",
  "base_s3_bucket_url",
  "pmpSupport",
  "setPasswordUrl",
];

/**
 * Custom Email Templates admin page (EN-5162), migrated from the Retool
 * EmailTemplates app: pick hospital → ward → template type, edit the HTML
 * with a live preview, save, or send a test email.
 *
 * ⚠️ The endpoint route reads `Hospital/{hospitalId}` but takes a **ward**
 * id — see `emailTemplatesService`.
 *
 * The test send is the one outbound action in /admin. It lives behind
 * `SendTestEmailModal` and is never wired to a select change or an effect.
 */
export function AdminEmailTemplates() {
  document.title = "Email templates - Admin";

  const { openModal, closeModal } = useModal();
  const { showToast } = useToast();
  const { hospitals, isLoading: hospitalsLoading } = useHospitalOptions();

  const [hospitalId, setHospitalId] = useState<number | undefined>();
  const [wardId, setWardId] = useState<number | undefined>();
  const [templateType, setTemplateType] = useState<IEmailTemplateType | "">("");

  const { wards, isLoading: wardsLoading } = useWardOptions(hospitalId);
  const { emailTemplate, isLoading, isError, isFetched } = useEmailTemplate(
    wardId,
    templateType
  );
  const { saveEmailTemplate, isSavingTemplate } = useSaveEmailTemplate();

  const [subject, setSubject] = useState("");
  const [html, setHtml] = useState("");
  const [publish, setPublish] = useState(true);

  // Seed the form whenever a different template loads. Keyed on the record's
  // id so re-fetching the same one doesn't stamp over an in-progress edit.
  const [seededId, setSeededId] = useState<string | null>(null);
  const [hasEdits, setHasEdits] = useState(false);
  useEffect(() => {
    if (!emailTemplate || emailTemplate.id === seededId) return;
    setSubject(emailTemplate.subjectLine ?? "");
    setHtml(emailTemplate.savedTemplate ?? "");
    setPublish(emailTemplate.publish);
    setSeededId(emailTemplate.id);
    setHasEdits(false);
  }, [emailTemplate, seededId]);

  const wardName = wards.find((ward) => ward.id === wardId)?.name ?? "";
  const hasSelection = wardId !== undefined && templateType !== "";
  const notFound = hasSelection && isFetched && !emailTemplate;

  const handleHospitalChange = (value: string) => {
    setHospitalId(value ? Number(value) : undefined);
    // The ward list is scoped to the hospital, so the old ward can't survive.
    setWardId(undefined);
    setSeededId(null);
  };

  // Changing any selector reseeds the form, which would silently stamp over
  // an in-progress edit — a long HTML template is expensive to lose, so a
  // dirty form gets a real confirmation first (same convention as the
  // translations pages' DiscardTranslationsModal).
  const requestSelectionChange = (apply: () => void) => {
    if (!hasEdits) {
      apply();
      return;
    }
    openModal(
      <ModalContainer>
        <ModalTitle>Discard unsaved template changes?</ModalTitle>
        <ModalForm>
          <WarningNotice>
            You have unsaved changes to this email template. Switching discards
            them. This can&apos;t be undone.
          </WarningNotice>
          <ModalFormActions>
            <FilterButton variant="secondary" onClick={closeModal}>
              Keep editing
            </FilterButton>
            <FilterButton
              variant="primary"
              onClick={() => {
                setHasEdits(false);
                apply();
                closeModal();
              }}
            >
              Discard and switch
            </FilterButton>
          </ModalFormActions>
        </ModalForm>
      </ModalContainer>,
      { width: "480px", showCloseButton: true }
    );
  };

  const handleSave = async () => {
    if (!wardId || !templateType) return;

    try {
      await saveEmailTemplate({
        wardId,
        templateType,
        request: {
          savedTemplate: html,
          subjectLine: subject,
          // Retool also sent `baseTemplate`, which the DTO has no field for
          // and the server ignores. Only what the contract accepts is sent.
          templateKeyValues: emailTemplate?.templateKeyValues ?? null,
          publish,
        },
      });
      setHasEdits(false);
      showToast("The email template was updated successfully.", "success");
    } catch (error) {
      showToast(
        errorToastMessage(
          error,
          "There was an error updating this email template."
        ),
        "warning"
      );
    }
  };

  const handleSendTest = () => {
    openModal(
      <SendTestEmailModal
        subject={subject}
        html={html}
        wardName={wardName || `ward ${wardId}`}
        onClose={closeModal}
      />,
      { width: "480px", showCloseButton: true }
    );
  };

  return (
    <PageContainer>
      <PageHeader>
        <PageTitle>Email templates</PageTitle>
      </PageHeader>

      <FiltersContainer>
        <FilterGroup>
          <FilterLabel htmlFor="email-template-hospital">Hospital</FilterLabel>
          <AdminSelectField
            id="email-template-hospital"
            value={hospitalId ?? ""}
            disabled={hospitalsLoading}
            onChange={(e) => {
              const { value } = e.target;
              requestSelectionChange(() => handleHospitalChange(value));
            }}
          >
            <option value="">
              {hospitalsLoading ? "Loading..." : "Select a hospital"}
            </option>
            {hospitals.map((hospital) => (
              <option key={hospital.id} value={hospital.id}>
                {hospital.name}
              </option>
            ))}
          </AdminSelectField>
        </FilterGroup>

        <FilterGroup>
          <FilterLabel htmlFor="email-template-ward">Ward</FilterLabel>
          <AdminSelectField
            id="email-template-ward"
            value={wardId ?? ""}
            disabled={hospitalId === undefined || wardsLoading}
            onChange={(e) => {
              const { value } = e.target;
              requestSelectionChange(() => {
                setWardId(value ? Number(value) : undefined);
                setSeededId(null);
              });
            }}
          >
            <option value="">
              {(() => {
                if (hospitalId === undefined) return "Pick a hospital first";
                return wardsLoading ? "Loading..." : "Select a ward";
              })()}
            </option>
            {wards.map((ward) => (
              <option key={ward.id} value={ward.id}>
                {ward.name}
              </option>
            ))}
          </AdminSelectField>
        </FilterGroup>

        <FilterGroup>
          <FilterLabel htmlFor="email-template-type">Template type</FilterLabel>
          <AdminSelectField
            id="email-template-type"
            value={templateType}
            onChange={(e) => {
              const { value } = e.target;
              requestSelectionChange(() => {
                setTemplateType(value as IEmailTemplateType | "");
                setSeededId(null);
              });
            }}
          >
            <option value="">Select a type</option>
            {TEMPLATE_TYPES.map((type) => (
              <option key={type} value={type}>
                {type}
              </option>
            ))}
          </AdminSelectField>
        </FilterGroup>
      </FiltersContainer>

      {isError && (
        <ErrorMessage>
          Failed to load this template. Check the selection and try again.
        </ErrorMessage>
      )}

      {!hasSelection && (
        <SelectionPrompt>
          Choose a hospital, a ward and a template type to edit its email
          template.
        </SelectionPrompt>
      )}

      {hasSelection && isLoading && (
        <SelectionPrompt>Loading the template...</SelectionPrompt>
      )}

      {notFound && (
        <SelectionPrompt>
          This ward has no {templateType.toLowerCase()} template yet.
        </SelectionPrompt>
      )}

      {hasSelection && emailTemplate && (
        <TemplateCard>
          <SubjectRow>
            <SubjectField>
              <FilterLabel htmlFor="email-template-subject">
                Subject
              </FilterLabel>
              <FilterInput
                id="email-template-subject"
                type="text"
                value={subject}
                disabled={isSavingTemplate}
                onChange={(e) => {
                  setSubject(e.target.value);
                  setHasEdits(true);
                }}
              />
            </SubjectField>

            <VisibilityField>
              <FilterLabel>Visibility</FilterLabel>
              <VisibilityControl>
                <CheckboxField>
                  <input
                    type="checkbox"
                    checked={publish}
                    disabled={isSavingTemplate}
                    onChange={(e) => {
                      setPublish(e.target.checked);
                      setHasEdits(true);
                    }}
                  />
                  Published
                </CheckboxField>
                <FieldHint>
                  An unpublished template is not sent — the default is used
                  instead.
                </FieldHint>
              </VisibilityControl>
            </VisibilityField>
          </SubjectRow>

          <SplitPane>
            <LongTextEditor
              id="email-template-html"
              label="Template HTML"
              value={html}
              tall
              disabled={isSavingTemplate}
              variables={TEMPLATE_VARIABLES}
              hint="Stored exactly as typed. Click a placeholder to insert it at the cursor."
              onChange={(next) => {
                setHtml(next);
                setHasEdits(true);
              }}
            />
            <HtmlPreview
              html={html}
              title="Email template preview"
              hint="Rendered in a sandboxed frame — scripts never run, and placeholders are not substituted."
            />
          </SplitPane>
          {/*
            Origin, warning and actions sit under the editor rather than above
            it (EN-5146 UI feedback): they are all about what Save is going to
            do, and at the top they pushed the field the page exists for
            below the fold.
          */}
          <TemplateCardFooter>
            {!emailTemplate.isHospitalSpecific && (
              <WarningNotice>
                You are editing the shared default. Saving writes a copy for
                this ward only — other wards keep the default.
              </WarningNotice>
            )}
            <TemplateCardActionRow>
              <TemplateOrigin>
                {emailTemplate.isHospitalSpecific
                  ? `${wardName || `Ward ${wardId}`} · ward-specific template`
                  : "Shared default — saving creates this ward's own copy"}
              </TemplateOrigin>
              <TemplateCardActions>
                <FilterButton
                  variant="secondary"
                  disabled={isSavingTemplate}
                  onClick={handleSendTest}
                >
                  Send test email
                </FilterButton>
                <FilterButton
                  variant="primary"
                  disabled={isSavingTemplate}
                  onClick={handleSave}
                >
                  {isSavingTemplate ? "Saving..." : "Save"}
                </FilterButton>
              </TemplateCardActions>
            </TemplateCardActionRow>
          </TemplateCardFooter>
        </TemplateCard>
      )}
    </PageContainer>
  );
}
